import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.refs.addToCartButton.form || this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.hasAttribute('data-added')) {
      addToCartButton.setAttribute('data-added', '');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #isSubmitting = false;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Get the button that triggered the submit
    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);

    // Check if already submitting
    if (this.#isSubmitting) return;

    // Check if the add to cart button is disabled
    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (addToCartButton?.getAttribute('disabled') === 'true') return;

    this.#isSubmitting = true;

    // Animate the submitter button immediately (optimistic UI)
    // Skip if already animated (e.g., by AddToCartComponent.handleClick)
    if (submitter && !submitter.hasAttribute('data-added')) {
      this.#animateAddedState(submitter);
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // Disable the add to cart button since the stock limit has been reached
          const container = this.refs.addToCartButtonContainer;
          if (container && typeof container.disable === 'function') {
            container.disable();
          }

          // Also disable the sticky buy button's add-to-cart
          const formElement = this.querySelector('form');
          const formId = formElement?.getAttribute('id');
          if (formId) {
            const stickyBuyButton = document.querySelector(`sticky-buy-button[data-target-id="${CSS.escape(formId)}"]`);
            const stickyButton = stickyBuyButton?.querySelector('button[type="submit"]');
            if (stickyButton instanceof HTMLButtonElement) {
              stickyButton.disabled = true;
            }
          }

          // Dispatch a cart update event so the cart icon can fetch the actual count
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          const addedTextElement = submitter?.querySelector('.add-to-cart-button-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#isSubmitting = false;
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Animates the "Added" state on a button.
   * @param {HTMLButtonElement} button
   */
  #animateAddedState(button) {
    if (button.hasAttribute('data-added')) return;

    button.setAttribute('data-added', '');

    setTimeout(() => {
      button.removeAttribute('data-added');
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    // Check if variant picker still requires user selection
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    const requiresSelection = variantPicker?.hasAttribute('data-requires-selection');

    // Find THIS product-form-component in the re-rendered HTML to get the correct button container
    // We use the form ID to match the right product form (there may be multiple on the page)
    const formElement = this.querySelector('form');
    // Use getAttribute('id') because form.id can return an input element if there's <input name="id">
    const formId = formElement?.getAttribute('id');
    let newAddToCartButtonContainer;

    if (formId) {
      // Find the product-form-component that contains a form with the matching ID
      // Use CSS.escape to handle special characters in IDs
      const formSelector = `form#${CSS.escape(formId)}`;
      const foundForm = event.detail.data.html.querySelector(formSelector);
      const newProductForm = foundForm?.closest('product-form-component');
      newAddToCartButtonContainer = newProductForm?.querySelector('[ref="addToCartButtonContainer"]');
    }

    // Fallback: if no form ID, try to find by section ID
    if (!newAddToCartButtonContainer) {
      const sectionId = this.closest('.shopify-section')?.id;
      if (sectionId) {
        const newSection = event.detail.data.html.querySelector(`#${sectionId}`);
        const newProductForm = newSection?.querySelector('product-form-component');
        newAddToCartButtonContainer = newProductForm?.querySelector('[ref="addToCartButtonContainer"]');
      }
    }

    // Final fallback: get the first one (original behavior)
    if (!newAddToCartButtonContainer) {
      newAddToCartButtonContainer = event.detail.data.html.querySelector('[ref="addToCartButtonContainer"]');
    }

    if (addToCartButtonContainer && newAddToCartButtonContainer) {
      // Check if we're switching between add-to-cart and back-in-stock modes
      const currentIsBackInStock = addToCartButtonContainer.hasAttribute('data-back-in-stock');
      const newIsBackInStock = newAddToCartButtonContainer.hasAttribute('data-back-in-stock');

      if (currentIsBackInStock !== newIsBackInStock) {
        // Component type is changing - replace the entire container
        const parent = addToCartButtonContainer.parentNode;
        if (parent) {
          parent.replaceChild(newAddToCartButtonContainer, addToCartButtonContainer);
          // Re-query refs after replacement
          queueMicrotask(() => this.updatedCallback());
        }
      } else {
        // Same component type, morph the button
        const currentAddToCartButton = addToCartButtonContainer.querySelector('[ref="addToCartButton"]');
        const newAddToCartButton = newAddToCartButtonContainer.querySelector('[ref="addToCartButton"]');

        if (currentAddToCartButton && newAddToCartButton) {
          morph(currentAddToCartButton, newAddToCartButton);
        }

        // Update the button state for add-to-cart-component (back-in-stock-button doesn't have disable/enable)
        if (!newIsBackInStock && addToCartButtonContainer.disable && addToCartButtonContainer.enable) {
          if (event.detail.resource == null || event.detail.resource.available == false || requiresSelection) {
            addToCartButtonContainer.disable();
            this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
          } else {
            addToCartButtonContainer.enable();
            this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
          }
        }

        // Set the data attribute for the add to cart button to the product variant media if it exists
        if (event.detail.resource) {
          const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
          productVariantMedia &&
            addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
        }
      }
    }

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Update the cart notification template with the new variant content
    const currentTemplate = this.querySelector('[ref="cartNotificationContent"]');
    const newTemplate = event.detail.data.html.querySelector('[ref="cartNotificationContent"]');
    if (currentTemplate && newTemplate) {
      currentTemplate.innerHTML = newTemplate.innerHTML;
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    // Only call disable if it's an add-to-cart-component (back-in-stock-button doesn't have this method)
    const container = this.refs.addToCartButtonContainer;
    if (container && typeof container.disable === 'function') {
      container.disable();
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
